import React from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useFormContext, Controller } from 'react-hook-form';
import { QuestionData } from '../types';

interface SelectSingleFieldProps {
  question: QuestionData;
  placeholder?: string;
  disabled?: boolean;
}

const SelectSingleFieldComponent: React.FC<SelectSingleFieldProps> = ({
  question,
  placeholder,
  disabled,
}) => {
  const { register, control, trigger } = useFormContext();

  if (!question.options) {
    return (
      <Input
        placeholder="No options available"
        className="w-full"
        disabled={disabled}
      />
    );
  }

  // Use radio buttons if there are exactly 2 options
  if (question.options.length === 2) {
    return (
      <Controller
        name={question.slug}
        control={control}
        render={({ field }) => (
          <RadioGroup
            onValueChange={value => {
              field.onChange(value);
              trigger(question.slug);
            }}
            value={field.value}
            className="flex gap-6"
          >
            {question.options!.map(option => (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem
                  value={option.value}
                  id={`${question.slug}-${option.value}`}
                  disabled={disabled}
                />
                <Label
                  htmlFor={`${question.slug}-${option.value}`}
                  className="cursor-pointer"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      />
    );
  }

  // Use dropdown for more than 2 options
  return (
    <select
      {...register(question.slug)}
      id={question.slug}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {question.options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const createSelectSingleSchema = (question: QuestionData): z.ZodTypeAny => {
  if (question.required) {
    return z.string().min(1, 'This field is required');
  }
  return z.string().optional();
};

const getDefaultValue = (): string => '';

export const SelectSingleField = {
  component: SelectSingleFieldComponent,
  createSchema: createSelectSingleSchema,
  getDefaultValue: () => getDefaultValue(),
};
